import BreadCrumb from "Components/Common/BreadCrumb";
import React, { useMemo } from "react";
import { Container, Row } from "reactstrap";
import BasicInformation from "./BasicInformation";
import NavTabs from "./NavTabs";
import { builderDetailWidgets } from "common/data/builder";
import Widgets from "Components/Common/Widgets";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { builderPropertiesCount } from "endpoints/builder/builderDetails.action";

const BuilderDetail = () => {

  document.title = "Builder Detail";

  const builderId = useSelector((state: any) => state.Builder.selectedBuilderId);

  const { data: builderPropertiesData, isPending, isError, isFetching } = useQuery({
    queryKey: ['builderPropertiesCount', builderId],
    queryFn: () => builderPropertiesCount(builderId),
    enabled: !!builderId, // Only fetch when builderId is available
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  // Transform API data to widget format
  const dynamicWidgets = useMemo(() => {
    if (!builderPropertiesData?.data) {
      return builderDetailWidgets; // Return static data as fallback
    }

    const apiData = builderPropertiesData.data;

    // Helper function to format numbers with commas
    const formatNumber = (num: number | string | undefined): string => {
      if (!num) return "0";
      const numValue = typeof num === 'string' ? parseInt(num) : num;
      return numValue.toLocaleString('en-IN'); // Indian formatting with commas
    };

    return [
      {
        id: 1,
        icon: "ri-building-4-line",
        label: "Total Properties",
        counter: formatNumber(apiData.totalCount),
        badge: "ri-arrow-up-s-fill",
        badgeColor: "success",
        percentage: "6.24",
        decimal: "2",
        prefix: "",
        separator: ",",
      },
      {
        id: 2,
        icon: "ri-check-fill",
        label: "Total Rented",
        counter: formatNumber(apiData.rented),
        badge: "ri-arrow-up-s-fill",
        badgeColor: "success",
        percentage: "3.67",
        decimal: "2",
        prefix: "",
        separator: ",",
      },
      {
        id: 3,
        icon: "ri-error-warning-line",
        label: "Total Sold",
        counter: formatNumber(apiData.sold),
        percentage: "4.80",
        decimal: "2",
        prefix: "",
        separator: ",",
      },
      {
        id: 4,
        icon: "ri-error-warning-line",
        label: "Rejected",
        counter: formatNumber(apiData.rejected),
        percentage: "4.80",
        decimal: "2",
        prefix: "",
        separator: ",",
      },
    ];
  }, [builderPropertiesData]);

  // Show loading state only when there's no data and we're fetching
  if ((isPending || isFetching) && !builderPropertiesData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show error state  
  if (isError) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="text-danger mb-3">
            <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
          </div>
          <h5 className="text-danger">Failed to load builder data</h5>
          <p className="text-muted">Please refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* <BreadCrumb title="Builder Detail" pageTitle="Builder" /> */}
          <BreadCrumb
            title="Builder Detail"
            pageTitles={[
              { label: "Builder", path: "/builder" },
              { label: "Builder Detail" }
            ]}
          />
          <Row>
            <Widgets data={dynamicWidgets} />
          </Row>
          <div>
            <BasicInformation id={builderId} />
          </div>
          <div>
            <NavTabs />
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BuilderDetail;
